import axios from 'axios';

export const API_URL = import.meta.env?.VITE_API_URL || '/api';

type RefreshResponse = {
  success: boolean;
  data?: { bwauthTokens?: { accessToken?: unknown; refreshToken?: unknown } };
};

// A separate client keeps refresh failures out of the normal 401 interceptor.
const refreshClient = axios.create({ baseURL: API_URL, timeout: 30000 });
let pendingRefresh: { refreshToken: string; promise: Promise<string> } | null = null;

export function getSessionErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      return 'O tempo de resposta foi excedido. Tente novamente.';
    }
    if (!error.response) {
      return 'Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.';
    }
    if (error.response.status === 401 || error.response.status === 403) {
      return 'Sua sessão expirou. Entre novamente para continuar.';
    }
    if (error.response.status === 429) {
      return 'Muitas tentativas em pouco tempo. Aguarde e tente novamente.';
    }
  }
  return 'Não foi possível verificar sua sessão. Tente novamente.';
}

export function expireSession(expectedToken: string | null) {
  // A late response must not clear a newer login or a refreshed session.
  if (localStorage.getItem('token') !== expectedToken) return;
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  if (window.location.pathname !== '/login') window.location.href = '/login';
}

export function isSessionEndpoint(url: string) {
  const path = new URL(url, window.location.origin).pathname.replace(/\/$/, '');
  return /\/auth\/(login|logout|refresh|forgot-password|reset-password|verify-reset-token|verify-mfa|verify-contact)$/.test(
    path,
  );
}

export async function refreshAccessToken(failedToken: string | null): Promise<string> {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('Sua sessão expirou. Entre novamente para continuar.');
  // Another request may have finished refreshing while this 401 was in flight.
  if (failedToken && token !== failedToken) return token;

  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    expireSession(token);
    throw new Error('Sua sessão expirou. Entre novamente para continuar.');
  }
  if (pendingRefresh?.refreshToken === refreshToken) return pendingRefresh.promise;

  const sessionUnchanged = () =>
    localStorage.getItem('token') === token &&
    localStorage.getItem('refreshToken') === refreshToken;

  const promise = (async () => {
    try {
      const { data } = await refreshClient.post<RefreshResponse>('/auth/refresh', { refreshToken });
      if (!sessionUnchanged()) throw new Error('A sessão foi alterada. Tente novamente.');

      const tokens = data?.data?.bwauthTokens;
      if (
        data?.success !== true ||
        typeof tokens?.accessToken !== 'string' ||
        !tokens.accessToken ||
        typeof tokens?.refreshToken !== 'string' ||
        !tokens.refreshToken
      ) {
        throw new Error('Não foi possível renovar a sessão. Tente novamente.');
      }

      localStorage.setItem('token', tokens.accessToken);
      localStorage.setItem('refreshToken', tokens.refreshToken);
      return tokens.accessToken;
    } catch (error) {
      if (
        sessionUnchanged() &&
        axios.isAxiosError(error) &&
        (error.response?.status === 401 || error.response?.status === 403)
      ) {
        expireSession(token);
      }
      if (error instanceof Error) error.message = getSessionErrorMessage(error);
      throw error;
    }
  })();

  pendingRefresh = { refreshToken, promise };
  try {
    return await promise;
  } finally {
    if (pendingRefresh?.promise === promise) pendingRefresh = null;
  }
}
